// src/store/slices/countrySlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { fetchCountries, addCountry, updateCountry } from './countryThunks'

export type CountryI18n = {
  fr?: string
  en?: string
  ar?: string
}

export type CountryValue = string | CountryI18n

export interface Country {
  id?: string
  _id?: string
  name: CountryValue
  nameLabel?: string
  indicatif: string
  mask: string
  code: string
}

export const getCountryDisplayName = (country?: Pick<Country, 'name' | 'nameLabel'> | null): string => {
  if (!country) return ''
  if (typeof country.nameLabel === 'string' && country.nameLabel.trim()) return country.nameLabel.trim()
  if (typeof country.name === 'string') return country.name.trim()
  if (country.name && typeof country.name === 'object') {
    return String(country.name.fr || country.name.en || country.name.ar || '').trim()
  }
  return ''
}

export const getCountryValueDisplay = (country?: CountryValue | null, fallback = '', lang = 'fr'): string => {
  if (typeof country === 'string') return country.trim() || fallback
  if (country && typeof country === 'object') {
    const normalizedLang = ['fr', 'en', 'ar'].includes(lang) ? lang as keyof CountryI18n : 'fr'
    return String(country[normalizedLang] || country.fr || country.en || country.ar || fallback || '').trim()
  }
  return fallback
}

interface CountryState {
  country: Country
  countries: Country[]
  loading: boolean
  error: string | null
}

const initialState: CountryState = {
  country: {
    name: '',
    indicatif: '',
    mask: '',
    code: ''
  },
  countries: [],
  loading: false,
  error: null
}

const countrySlice = createSlice({
  name: 'country',
  initialState,
  reducers: {
    setCountry: (state, action: PayloadAction<Country>) => {
      state.country = action.payload
    },
    resetCountry: (state) => {
      state.country = initialState.country
    }
  },
  extraReducers: (builder) => {
    builder
      // GET
      .addCase(fetchCountries.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCountries.fulfilled, (state, action: PayloadAction<Country[]>) => {
        state.loading = false
        state.countries = action.payload
      })
      .addCase(fetchCountries.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch countries'
      })

      // POST
      .addCase(addCountry.fulfilled, (state, action: PayloadAction<Country>) => {
        state.countries.push(action.payload)
      })

      // PUT
      .addCase(updateCountry.fulfilled, (state, action: PayloadAction<Country>) => {
        const index = state.countries.findIndex(c => c.code === action.payload.code)
        if (index !== -1) {
          state.countries[index] = action.payload
        }
      })
  }
})

export const { setCountry, resetCountry } = countrySlice.actions
export default countrySlice.reducer
